import React, { useEffect } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FolderIcon from "@mui/icons-material/Folder";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import CropRotateIcon from "@mui/icons-material/CropRotate";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import SendIcon from "@mui/icons-material/Send";
import PermIdIcon from "@mui/icons-material/PermIdentitySharp";

import useActions, { useAppDispatch, useAppSelector } from "../../app/hooks";
import IconButton from "@mui/material/IconButton";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import Tooltip from "@mui/material/Tooltip";
import { Workspace } from "../../types/Dash";
import { Folder } from "../../types/Folder";
import { Role } from "../../types/Role";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import DashInfoForm from "./DashInfoForm";
import DashCloneForm from "./DashCloneForm";
import DashMoveForm from "./DashMoveForm";
import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";
//import DashCreationForm from "../generation/DashCreationForm";

import AttachRoleForm from "./AttachRoleForm";
import { RollerShades } from "@mui/icons-material";

import Chip from "@mui/material/Chip";
import {
  selectFileFolderLabels,
  setFolderName,
  setRoleId,
  setUsersInRole,
} from "./librarySlice";

// Folder labels and how each renders. Borders per spec: LIVE_CHANGES red, DOCUMENTS
// blue, LEGACY/TO_DIE thick grey. ESTABLISHED has no border (distinguished by its chip).
const FOLDER_LABELS = ["LIVE_CHANGES", "ESTABLISHED", "DOCUMENTS", "LEGACY", "TO_DIE"];
const FOLDER_LABEL_DISPLAY: { [k: string]: string } = {
  LIVE_CHANGES: "Live changes",
  ESTABLISHED: "Established",
  DOCUMENTS: "Documents",
  LEGACY: "Legacy",
  TO_DIE: "To die",
};
const FOLDER_LABEL_BORDER: { [k: string]: string } = {
  LIVE_CHANGES: "2px solid #d32f2f",
  DOCUMENTS: "2px solid #1976d2",
  LEGACY: "4px solid #9e9e9e",
  TO_DIE: "4px solid #9e9e9e",
};
const FOLDER_LABEL_CHIP_COLOR: { [k: string]: "error" | "primary" | "success" | "default" } = {
  LIVE_CHANGES: "error",
  DOCUMENTS: "primary",
  ESTABLISHED: "success",
  LEGACY: "default",
  TO_DIE: "default",
};

/**
 * A single library item showing a Dash.
 *
 * @export
 * @param {{
 *   dash: Dash;
 * }} props
 * @return {*}
 */
export default function DashLibraryFileFolder(props: {
  workspaceRole: string;
  file_folder: string;
  repoId: number;
  workspaces: Array<Workspace> | null;
  currentWorkspace: Workspace | null;
  folderName: string;
}) {
  const actions = useActions();
  const dispatch = useAppDispatch();

  const fullFolder = (props.folderName === "/" ? "/" : props.folderName + "/") + props.file_folder;
  const folderLabels = useAppSelector(selectFileFolderLabels);
  const label = folderLabels[fullFolder] ?? "";
  const labelBorder = FOLDER_LABEL_BORDER[label];

  const handleSetLabel = (newLabel: string) => {
    setAnchorEl(null);
    actions.setFolderLabel({ repoId: props.repoId, folderPath: fullFolder, label: newLabel });
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const [infoOpen, setInfoOpen] = React.useState(false);

  const handleFolderClicked = () => {

    dispatch(setFolderName(fullFolder));
    actions.fileList({id: props.repoId, folder: fullFolder});
    actions.gitInfo({id:props.repoId, folder:fullFolder, requestType: 'LOGS', count:10});
  };

  const handleInfoClicked = () => {
    setAnchorEl(null);
    setInfoOpen(true);
  };

  return (
      <ListItem
        sx={{
          bgcolor: "background.paper",
          marginBottom: "10px",
          width: "60vw",
          cursor: "pointer",
          ...(labelBorder ? { border: labelBorder, borderRadius: 1 } : {}),
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
        secondaryAction={
          <IconButton aria-label="comment"
            onClick={handleOpenMenu}>
            <FolderIcon />
          </IconButton>
        }
      >
        <ListItemText
          onClick={(e) => handleFolderClicked()}
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>{props.file_folder}</span>
              {label && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={FOLDER_LABEL_CHIP_COLOR[label] ?? "default"}
                  label={FOLDER_LABEL_DISPLAY[label] ?? label}
                />
              )}
            </Box>
          }
        />
      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}

      >
        <MenuItem onClick={handleInfoClicked}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Disk Folder Properties...</ListItemText>
        </MenuItem>

        <Divider />
        <MenuItem disabled>
          <ListItemText>Label folder</ListItemText>
        </MenuItem>
        {FOLDER_LABELS.map((l) => (
          <MenuItem key={l} selected={label === l} onClick={() => handleSetLabel(l)}>
            <ListItemText>{FOLDER_LABEL_DISPLAY[l]}</ListItemText>
          </MenuItem>
        ))}
        <MenuItem onClick={() => handleSetLabel("")} disabled={!label}>
          <ListItemText>Clear label</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Disk folder properties</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <InfoRow label="Folder name" value={props.file_folder} />
            <InfoRow label="Repo id" value={String(props.repoId)} />
            <InfoRow label="Parent folder" value={props.folderName} />
            <InfoRow
              label="Full path"
              value={(props.folderName === "/" ? "/" : props.folderName + "/") + props.file_folder}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </ListItem>
    );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
        {value}
      </Typography>
    </Box>
  );
}
